// „Ranking graczy” (patch-35): kto z rodzeństwa zdobył najwięcej punktów (gwiazdek za wymowę). Dwie zakładki: „Ten tydzień” i „Wszystkie czasy”.
// Układ z makiety: nagłówek (profil + liczby), strzałka powrotu, tytuł z podtekstem, przełącznik zakładek, podium (2 · 1 z koroną · 3) i lista od 4. miejsca.
// Na podium stają tylko gracze z punktami (od patcha 49): jeden, dwóch albo trzech; kto ma 0 punktów, nie ma miejsca — jest na końcu listy z kreską zamiast numeru.
// Punkty liczy serwer (`GET /api/ranking`); wynik tego dziecka bierzemy z jego własnego postępu na urządzeniu, więc jest zawsze świeży.
// Napisy idą za przełącznikiem EN/PL (jak reszta ekranów dziecka).
import type { Pair } from '../lib/i18n.ts';
import { rankPlayers, totalPoints, weekPoints } from '../lib/ranking.ts';
import type { Placed, RankPlayer, RankScope } from '../lib/ranking.ts';
import type { RankingResult } from '../lib/profilesApi.ts';
import { avatarEl } from './avatars.ts';
import type { AppCtx } from './ctx.ts';
import { button, h } from './dom.ts';
import { renderHomeHead } from './homeHead.ts';
import { langOf, rerenderOnLang, tr, txText } from './i18n.ts';
import { icon } from './kit.ts';

const T = {
  title: { pl: 'Ranking graczy', en: 'Player ranking' },
  subWeek: { pl: 'Zobacz, kto zdobył najwięcej punktów w tym tygodniu!', en: 'See who earned the most points this week!' },
  subAll: { pl: 'Zobacz, kto zdobył najwięcej punktów od początku!', en: 'See who has earned the most points of all time!' },
  tabWeek: { pl: 'Ten tydzień', en: 'This week' },
  tabAll: { pl: 'Wszystkie czasy', en: 'All time' },
  tabs: { pl: 'Zakres rankingu', en: 'Ranking range' },
  back: { pl: 'Wróć', en: 'Back' },
  loading: { pl: 'Ładuję ranking…', en: 'Loading the ranking…' },
  offline: { pl: 'Brak internetu — ranking pokaże się, gdy wróci.', en: 'No internet — the ranking will show up when it is back.' },
  error: { pl: 'Nie udało się pobrać rankingu.', en: 'Could not load the ranking.' },
  noAccess: { pl: 'Brak dostępu do serwera.', en: 'No access to the server.' },
  retry: { pl: 'Spróbuj jeszcze raz', en: 'Try again' },
  empty: { pl: 'Jeszcze nikt nie ma punktów — zrób lekcję i wskocz na podium!', en: 'Nobody has points yet — do a lesson and jump onto the podium!' },
  you: { pl: 'Ty', en: 'You' },
  crown: { pl: 'Korona dla pierwszego miejsca', en: 'Crown for first place' },
} as const satisfies Record<string, Pair>;

const BTN: Pair = { pl: '🏆 Ranking graczy', en: '🏆 Player ranking' };

const pts = (n: number, lang: 'en' | 'pl'): string => `${n} ${lang === 'en' ? 'pts' : 'pkt'}`;

export type State = { kind: 'loading' } | { kind: 'ok'; players: RankPlayer[] } | { kind: 'offline' | 'error' | 'unpaired' | 'no-server' };

/** Wynik serwera → stan ekranu; punkty tego dziecka są z jego urządzenia (serwer mógł nie dostać jeszcze ostatniej lekcji). */
export function stateFrom(ctx: AppCtx, r: RankingResult): State {
  if (r.kind !== 'ok') return { kind: r.kind === 'no-server' ? 'no-server' : r.kind };
  const me = ctx.account?.profile()?.id;
  if (!me) return { kind: 'ok', players: r.players };
  const progress = ctx.getProgress();
  const mine = { week: weekPoints(progress, ctx.today()), total: totalPoints(progress) };
  const players = r.players.map((p) => (p.id === me ? { ...p, week: Math.max(p.week, mine.week), total: Math.max(p.total, mine.total) } : p));
  return { kind: 'ok', players };
}

function crownEl(ctx: AppCtx): HTMLElement {
  const svg = `<svg viewBox="0 0 64 40" width="100%" height="100%" aria-hidden="true"><path d="M6 34 L2 10 L18 22 L32 4 L46 22 L62 10 L58 34 Z" fill="#f6c62d" stroke="#e0a800" stroke-width="2" stroke-linejoin="round"/><rect x="6" y="34" width="52" height="5" rx="2.5" fill="#e0a800"/><circle cx="32" cy="24" r="3.2" fill="#2b8a8a"/><circle cx="14" cy="26" r="2" fill="#fff" opacity=".8"/><circle cx="50" cy="26" r="2" fill="#fff" opacity=".8"/></svg>`;
  const el = h('span', { class: 'rk-crown', role: 'img', 'data-testid': 'rk-crown' });
  el.innerHTML = svg;
  el.setAttribute('aria-label', tr(ctx, T.crown));
  return el;
}

function podiumSlot(ctx: AppCtx, p: Placed, me: string | undefined, lang: 'en' | 'pl', crown: boolean): HTMLElement {
  const place = Math.min(3, p.place);
  const medal = h('span', { class: `rk-medal m${place}` }, String(p.place));
  return h('div', { class: `rk-slot s${place}${p.id === me ? ' me' : ''}`, 'data-testid': `rk-podium-${p.id}`, 'data-place': p.place, 'data-points': p.points, 'data-me': p.id === me ? 'true' : undefined },
    h('div', { class: 'rk-avwrap' },
      crown ? crownEl(ctx) : null,
      h('div', { class: 'rk-av' }, avatarEl({ avatar: p.avatar, theme: p.theme }, 'lg', ctx), medal),
    ),
    h('div', { class: 'rk-col' },
      h('span', { class: 'rk-name' }, p.name, p.id === me ? h('small', { class: 'rk-you' }, tr(ctx, T.you)) : null),
      h('span', { class: 'rk-pts' }, icon('starFill'), pts(p.points, lang)),
    ),
  );
}

function listRow(ctx: AppCtx, p: Placed, me: string | undefined, lang: 'en' | 'pl', showPlace: boolean): HTMLElement {
  return h('li', { class: `rk-row${p.id === me ? ' me' : ''}`, 'data-testid': `rk-row-${p.id}`, 'data-place': showPlace ? p.place : undefined, 'data-points': p.points, 'data-me': p.id === me ? 'true' : undefined },
    h('span', { class: 'rk-rank' }, showPlace ? `#${p.place}` : '–'),
    avatarEl({ avatar: p.avatar, theme: p.theme }, 'md', ctx),
    h('span', { class: 'rk-rname' }, p.name, p.id === me ? h('small', { class: 'rk-you' }, tr(ctx, T.you)) : null),
    h('span', { class: 'rk-chip' }, icon('starFill'), pts(p.points, lang)),
  );
}

/**
 * Podium i lista dla wybranej zakładki. Podium: do trzech graczy Z PUNKTAMI (korona dla każdego, kto ma 1. miejsce — także przy remisie); lista: reszta z punktami
 * (numer miejsca; remis na granicy podium zachowuje wspólny numer) i na końcu gracze bez punktów (kreska zamiast numeru). Gdy nikt jeszcze nie ma punktów, zamiast podium
 * jest zachęta i sama lista graczy.
 */
export function rankingBody(ctx: AppCtx, players: readonly RankPlayer[], scope: RankScope): HTMLElement {
  const lang = langOf(ctx);
  const me = ctx.account?.profile()?.id;
  const placed = rankPlayers(players, scope);
  const scoring = placed.filter((p) => p.place > 0);
  const unranked = placed.filter((p) => p.place === 0);
  const body = h('div', { class: 'rk-content', 'data-testid': 'ranking-list' });
  if (placed.length === 0) return body;
  if (scoring.length === 0) {
    body.append(h('p', { class: 'rk-empty', 'data-testid': 'rk-empty' }, tr(ctx, T.empty)), h('ol', { class: 'rk-list' }, ...unranked.map((p) => listRow(ctx, p, me, lang, false))));
    return body;
  }
  const [first, second, third] = scoring;
  const slots = [second, first, third].filter((p): p is Placed => p !== undefined);
  body.append(h('div', { class: 'rk-podium', 'data-testid': 'rk-podium', 'data-slots': slots.length }, ...slots.map((p) => podiumSlot(ctx, p, me, lang, p.place === 1))));
  const rows = [...scoring.slice(3).map((p) => listRow(ctx, p, me, lang, true)), ...unranked.map((p) => listRow(ctx, p, me, lang, false))];
  if (rows.length > 0) body.append(h('ol', { class: 'rk-list' }, ...rows));
  return body;
}

export function renderRanking(ctx: AppCtx, scope: RankScope = 'week', known?: State): void {
  ctx.leaveHome();
  const acc = ctx.account;
  let current: RankScope = scope;
  let state: State = known ?? { kind: 'loading' };
  ctx.at?.({ screen: 'ranking', scope: current });

  const back = button(icon('back'), () => ctx.goHome(), { class: 'icon-btn', testid: 'ranking-back', title: tr(ctx, T.back) });
  back.setAttribute('aria-label', tr(ctx, T.back));
  const sub = h('p', { class: 'rk-sub', 'data-testid': 'rk-sub' });
  const tabWeek = h('button', { type: 'button', role: 'tab', class: 'rk-tab', 'data-testid': 'rk-tab-week', onclick: () => choose('week') }, tr(ctx, T.tabWeek));
  const tabAll = h('button', { type: 'button', role: 'tab', class: 'rk-tab', 'data-testid': 'rk-tab-all', onclick: () => choose('all') }, tr(ctx, T.tabAll));
  const tabs = h('div', { class: 'rk-tabs', role: 'tablist', 'aria-label': tr(ctx, T.tabs), 'data-testid': 'rk-tabs' }, tabWeek, tabAll);
  const content = h('div', { class: 'rk-slotbox', 'data-testid': 'ranking-body' });

  const load = (): void => {
    state = { kind: 'loading' };
    paint();
    if (!acc) {
      state = { kind: 'no-server' };
      paint();
      return;
    }
    void acc.ranking(ctx.today()).then((r) => {
      if ((main as { isConnected?: boolean }).isConnected === false) return;
      state = stateFrom(ctx, r);
      paint();
    });
  };

  function paint(): void {
    main.setAttribute('data-scope', current);
    sub.textContent = tr(ctx, current === 'week' ? T.subWeek : T.subAll);
    for (const [tab, id] of [[tabWeek, 'week'], [tabAll, 'all']] as const) {
      const on = current === id;
      tab.classList.toggle('on', on);
      tab.setAttribute('aria-selected', String(on));
      tab.setAttribute('tabindex', on ? '0' : '-1');
    }
    switch (state.kind) {
      case 'loading':
        content.replaceChildren(h('p', { class: 'rk-note', 'data-testid': 'rk-loading' }, tr(ctx, T.loading)));
        break;
      case 'ok':
        content.replaceChildren(rankingBody(ctx, state.players, current));
        break;
      default:
        content.replaceChildren(
          h('p', { class: 'rk-note', 'data-testid': 'rk-problem', 'data-kind': state.kind }, tr(ctx, state.kind === 'offline' ? T.offline : state.kind === 'error' ? T.error : T.noAccess)),
          ...(state.kind === 'no-server' ? [] : [button(tr(ctx, T.retry), load, { class: 'btn ghost', testid: 'rk-retry' })]),
        );
    }
  }

  function choose(next: RankScope): void {
    if (next === current) return;
    current = next;
    ctx.at?.({ screen: 'ranking', scope: current });
    paint();
  }

  const spark = h('span', { class: 'rk-spark', 'aria-hidden': 'true' });
  spark.innerHTML = '<svg viewBox="0 0 40 44" width="100%" height="100%"><g stroke="#5b57e6" stroke-width="4" stroke-linecap="round" fill="none"><path d="M10 8 L22 2"/><path d="M12 22 L30 22"/><path d="M10 36 L22 42"/></g></svg>';
  const main = h('main', { class: 'stage ranking', 'data-testid': 'ranking-screen', 'data-scope': current },
    renderHomeHead(ctx),
    h('div', { class: 'rk-wrap' },
      h('div', { class: 'rk-nav' }, back),
      h('div', { class: 'rk-heading' }, h('h1', { class: 'rk-title', 'data-testid': 'rk-title' }, tr(ctx, T.title), ' ', spark), sub),
      tabs,
      content,
    ),
  );
  ctx.root.replaceChildren(main);
  paint();
  if (known === undefined) load();
  // zmiana języka (EN ⌄) przepisuje napisy od razu; dane zostają, więc nie pytamy serwera drugi raz
  rerenderOnLang(ctx, main, () => renderRanking(ctx, current, state.kind === 'loading' ? undefined : state));
}

/** Przycisk „🏆 Ranking graczy” przy „Zmień profil” na ekranie głównym — tylko w trybie rodziny (ranking potrzebuje serwera). */
export function renderRankingButton(ctx: AppCtx): HTMLElement | null {
  const acc = ctx.account;
  if (!acc || acc.mode() !== 'family') return null;
  return txText(ctx, button('', () => renderRanking(ctx), { class: 'btn ghost small rank-btn', testid: 'open-ranking' }), BTN);
}

